import { useEffect, useState } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useForm, Controller } from 'react-hook-form';
import { makeCall } from 'utils/utils';
import { Record, UpdateFormData as FormData, Currency } from './types';

type PropsType = {
  open: boolean;
  handleClose: () => void;
  record: Record | null;
  currencies: Currency[];
  refresh: () => void;
};

export default function Update({ open, handleClose, record, currencies, refresh }: PropsType) {
  const [isCopied, setIsCopied] = useState(false);
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
  } = useForm<FormData>();

  useEffect(() => {
    if (record) {
      const selectedCurrency = currencies.filter((row) => row.currencyCode === record.userCurrencyCode);
      reset({
        userFirstname: record.userFirstname,
        userLastname: record.userLastname,
        userEmail: record.userEmail,
        userPhone: record.userPhone,
        userActive: record.userActive,
        userCurrencyCode: selectedCurrency.length ? selectedCurrency[0] : null,
      });
    }
  }, [record, reset, currencies]);

  const onSubmit = async (data: FormData) => {
    const payload = {
      ...data,
      userCurrencyCode: data.userCurrencyCode?.currencyCode,
    };
    await makeCall(`users/${record?.userUuid}`, 'PUT', payload);
    close();
    refresh();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(record?.userUuid ?? '');
    setIsCopied(true);
  };

  const close = () => {
    setIsCopied(false);
    handleClose();
  };

  return (
    <div>
      <Dialog open={open} disableBackdropClick={true} onClose={close} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">Update User</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <DialogContentText>
              ID: {record?.userUuid}{' '}
              <Button disabled={isCopied} color="primary" onClick={handleCopy}>
                {isCopied ? 'Copied' : 'Copy'}
              </Button>
            </DialogContentText>
            <Controller
              name="userFirstname"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={errors.userFirstname ? true : false}
                  helperText={errors.userFirstname ? 'User first name is required!' : null}
                  autoFocus
                  margin="dense"
                  label="First Name"
                  fullWidth
                />
              )}
            />
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  error={errors.userLastname ? true : false}
                  helperText={errors.userLastname ? 'User last name is required!' : null}
                  margin="dense"
                  label="Last Name"
                  fullWidth
                />
              )}
              name="userLastname"
              control={control}
              rules={{ required: true }}
            />
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  error={errors.userEmail ? true : false}
                  helperText={errors.userEmail ? 'Valid email is required!' : null}
                  margin="dense"
                  label="Email Address"
                  fullWidth
                />
              )}
              name="userEmail"
              control={control}
              rules={{
                required: true,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: 'Valid email is required!',
                },
              }}
            />
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  error={errors.userPhone ? true : false}
                  helperText={errors.userPhone ? 'User phone is required!' : null}
                  margin="dense"
                  label="Phone"
                  fullWidth
                />
              )}
              name="userPhone"
              control={control}
              rules={{ required: true }}
            />
            <Controller
              name="userCurrencyCode"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  id="currency-select"
                  options={currencies}
                  onChange={(_, data) => field.onChange(data)}
                  getOptionLabel={(option) => option.currencyCode}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      error={errors.userCurrencyCode ? true : false}
                      helperText={errors.userCurrencyCode ? 'Currency code is required!' : null}
                      {...params}
                      label="Currency"
                      margin="dense"
                    />
                  )}
                />
              )}
            />
            <Controller
              name="userActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} defaultChecked={record?.userActive} color="primary" />}
                  label="Active"
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={close} color="primary">
              Cancel
            </Button>
            <Button disabled={isSubmitting} type="submit" color="primary">
              {isSubmitting ? 'Updating...' : 'Update'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}
